import React from 'react';

/**
 * TeacherPagination
 * Renders numbered pagination with prev/next arrows and ellipsis.
 *
 * Props:
 *   currentPage  – currently active page (1-indexed)
 *   totalPages   – total number of pages
 *   onPageChange – (page: number) => void
 */
export const TeacherPagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  /** Build array of page numbers + ellipsis keys. */
  const buildPages = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    let start = Math.max(2, currentPage - 1);
    let end   = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3)                 end = 4;
    if (currentPage >= totalPages - 2)    start = totalPages - 3;

    if (start > 2)             pages.push('ellipsis-start');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1)  pages.push('ellipsis-end');

    pages.push(totalPages);
    return pages;
  };

  const pages = buildPages();

  return (
    <div className="tp-pagination">
      {/* Previous */}
      <button
        className="tp-pagination__btn"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label="Previous page"
      >
        <i className="bi bi-chevron-left" />
      </button>

      {/* Page numbers */}
      {pages.map((page, idx) => {
        if (typeof page === 'string') {
          return (
            <span key={page} className="tp-pagination__ellipsis">…</span>
          );
        }
        return (
          <button
            key={page}
            className={`tp-pagination__btn ${currentPage === page ? 'tp-pagination__btn--active' : ''}`}
            onClick={() => onPageChange(page)}
            aria-label={`Page ${page}`}
            aria-current={currentPage === page ? 'page' : undefined}
          >
            {page}
          </button>
        );
      })}

      {/* Next */}
      <button
        className="tp-pagination__btn"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label="Next page"
      >
        <i className="bi bi-chevron-right" />
      </button>
    </div>
  );
};
